import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { ComplexModalTemplate } from '@connect-services/client/components/complexe-modal/complex-modal-template.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { ValidatorsService } from '@connect-services/client/services/validators.service';
import { InputTextComponent } from '@connect-services/src/lib/components/input/text/input-text/input-text.component';
import {
    ComputedStatistic,
    ComputedStatisticResult,
    StatisticRenderOptions,
    StatisticsService,
    UpdateStatisticDto,
} from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { FormService } from '@shared/client/services/form.service';
import AggregationMethodEnum = ComputedStatistic.AggregationMethodEnum;
import { firstValueFrom } from 'rxjs';

import { StatisticRenderOptionComponent } from '@connect-services/client/components/modals/create-edit-statistic-modal/statistic-render-option/statistic-render-option.component';
import { ChartComponent } from '@connect-services/client/components/statistics/chart/chart.component';
import { CheckboxComponent, CheckboxState } from '@connect-services/src/lib/components/checkbox/checkbox.component';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

class StatisticCreateEditForm extends FormGroup<{
    description: FormControl<string | null>;
    isShared: FormControl<boolean>;
    name: FormControl<string>;
    order: FormControl<number>;
    xAxisTitleText: FormControl<string | null>;
    xAxisUnit: FormControl<string | null>;
    yAxisTitleText: FormControl<string | null>;
    yAxisUnit: FormControl<string | null>;
}> {}

type StatisticCreateEditModalData = {
    isEditMode?: boolean;
    payloadStatistic: StatisticFromSocket;
    title: string;
};

type StatisticFromSocket = {
    aggregationMethod?: AggregationMethodEnum;
    description?: string;
    id?: string;
    isShared?: boolean;
    name: string;
    order?: number;
    previewResult?: ComputedStatisticResult;
    render?: ComputedStatistic.RenderEnum;
    renderOptions?: StatisticRenderOptions;
    sqlFunction?: string;
    standards?: string[];
    userId?: string;
};

export type AxisFormInputs = {
    controls: {
        xAxisTitleText: FormControl<string | null>;
        xAxisUnit: FormControl<string | null>;
        yAxisTitleText: FormControl<string | null>;
        yAxisUnit: FormControl<string | null>;
    };
    errors: {
        xAxisTitleText: string[];
        xAxisUnit: string[];
        yAxisTitleText: string[];
        yAxisUnit: string[];
    };
};

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ChartComponent,
        CheckboxComponent,
        ComplexModalTemplate,
        InputTextComponent,
        RessourcesModule,
        StatisticRenderOptionComponent,
    ],
    selector: 'app-create-edit-statistic-modal',
    templateUrl: './create-edit-statistic-modal.component.html',
})
export class CreateEditStatisticModalComponent implements OnInit {
    protected readonly axisFormInputs = computed<AxisFormInputs>(() => {
        return {
            controls: {
                xAxisTitleText: this.xAxisTitleTextControl,
                xAxisUnit: this.xAxisUnitControl,
                yAxisTitleText: this.yAxisTitleTextControl,
                yAxisUnit: this.yAxisUnitControl,
            },
            errors: {
                xAxisTitleText: this.xAxisTitleTextControl.errors
                    ? this.getFormErrors(this.xAxisTitleTextControl.errors)
                    : [],
                xAxisUnit: this.xAxisUnitControl.errors ? this.getFormErrors(this.xAxisUnitControl.errors) : [],
                yAxisTitleText: this.yAxisTitleTextControl.errors
                    ? this.getFormErrors(this.yAxisTitleTextControl.errors)
                    : [],
                yAxisUnit: this.yAxisUnitControl.errors ? this.getFormErrors(this.yAxisUnitControl.errors) : [],
            },
        };
    });
    protected readonly CheckboxState = CheckboxState;
    protected readonly ComputedStatistic = ComputedStatistic;
    protected readonly computedStatistic = computed(() => {
        return {
            aggregationMethod: this.data.payloadStatistic.aggregationMethod ?? null,
            render: this.data.payloadStatistic.render,
            renderOptions: this.data.payloadStatistic.renderOptions!,
            result: this.data.payloadStatistic.previewResult!,
        } as ComputedStatistic;
    });
    protected data: StatisticCreateEditModalData = inject(MAT_DIALOG_DATA);
    protected dialogRef = inject(MatDialogRef<CreateEditStatisticModalComponent>);
    protected readonly formService = inject(FormService);
    protected readonly isAxisConfigurationRequired = computed(() => {
        return (
            this.data.payloadStatistic.render === ComputedStatistic.RenderEnum.COLUMN ||
            this.data.payloadStatistic.render === ComputedStatistic.RenderEnum.LINE
        );
    });
    protected readonly statisticForm: WritableSignal<StatisticCreateEditForm | null> = signal(null);

    private readonly formBuilder = inject(FormBuilder);
    private readonly snackBarService = inject(SnackBarService);
    private readonly statisticsServiceSdk = inject(StatisticsService);

    protected get descriptionControl(): FormControl<string | null> {
        return this.statisticForm()!.controls.description;
    }

    protected get isSharedControl(): FormControl<boolean> {
        return this.statisticForm()!.controls.isShared;
    }

    protected get nameControl(): FormControl<string> {
        return this.statisticForm()!.controls.name;
    }

    protected get orderControl(): FormControl<number> {
        return this.statisticForm()!.controls.order;
    }

    protected get xAxisTitleTextControl(): FormControl<string | null> {
        return this.statisticForm()!.controls.xAxisTitleText;
    }

    protected get xAxisUnitControl(): FormControl<string | null> {
        return this.statisticForm()!.controls.xAxisUnit;
    }

    protected get yAxisTitleTextControl(): FormControl<string | null> {
        return this.statisticForm()!.controls.yAxisTitleText;
    }

    protected get yAxisUnitControl(): FormControl<string | null> {
        return this.statisticForm()!.controls.yAxisUnit;
    }

    public ngOnInit(): void {
        this.initForm();
    }

    protected getFormErrors(errors: ValidationErrors | null): string[] {
        if (!errors) {
            return [];
        }

        return Object.entries(errors).map(([key, value]) =>
            this.formService.getErrorMessage({
                key,
                value,
            }),
        );
    }

    protected async onClickEvent(modalResult: ModalResultEnum): Promise<void> {
        if (modalResult === ModalResultEnum.CANCEL) {
            this.dialogRef.close(null);

            return;
        }

        if (this.data.isEditMode) {
            await this.updateStatistic();

            return;
        }

        await this.createStatistic();
    }

    protected toggleIsShared(): void {
        this.isSharedControl.setValue(!this.isSharedControl.value);
    }

    private addStatisticFormBase(renderOptions: StatisticRenderOptions): void {
        this.statisticForm.set(
            this.formBuilder.group({
                description: new FormControl<string | null>(this.data.payloadStatistic.description ?? null, {
                    nonNullable: false,
                    validators: [ValidatorsService.maxLength(FormService.VARCHAR_COMMON_LENGTH)],
                }),
                isShared: new FormControl<boolean>(this.data.payloadStatistic.isShared ?? false, {
                    nonNullable: true,
                }),
                name: new FormControl<string>(this.data.payloadStatistic.name, {
                    nonNullable: true,
                    validators: ValidatorsService.stringValidationForm({
                        maxLength: FormService.VARCHAR_COMMON_LENGTH,
                        minLength: FormService.VARCHAR_MIN_LENGTH,
                    }),
                }),
                order: new FormControl<number>(this.data.payloadStatistic.order ?? 1, {
                    nonNullable: true,
                    validators: [Validators.min(1), ValidatorsService.integer()],
                }),
                xAxisTitleText: new FormControl<string | null>(renderOptions.xaxisTitle?.text ?? null, {
                    nonNullable: false,
                    validators: ValidatorsService.stringValidationForm({
                        maxLength: FormService.VARCHAR_COMMON_LENGTH,
                        minLength: FormService.VARCHAR_MIN_LENGTH,
                    }),
                }),
                xAxisUnit: new FormControl<string | null>(renderOptions.unit?.x ?? null, {
                    nonNullable: false,
                    validators: [ValidatorsService.maxLength(FormService.VARCHAR_COMMON_LENGTH)],
                }),
                yAxisTitleText: new FormControl<string | null>(renderOptions.yaxisTitle?.text ?? null, {
                    nonNullable: false,
                    validators: ValidatorsService.stringValidationForm({
                        maxLength: FormService.VARCHAR_COMMON_LENGTH,
                        minLength: FormService.VARCHAR_MIN_LENGTH,
                    }),
                }),
                yAxisUnit: new FormControl<string | null>(renderOptions.unit?.y ?? null, {
                    nonNullable: false,
                    validators: [ValidatorsService.maxLength(FormService.VARCHAR_COMMON_LENGTH)],
                }),
            }),
        );
    }

    private buildUpdatedRenderOptions(): StatisticRenderOptions {
        const current = this.data.payloadStatistic.renderOptions ?? {};

        if (!this.isAxisConfigurationRequired()) {
            return current;
        }

        return {
            ...current,
            unit: {
                x: this.xAxisUnitControl.value ?? current.unit?.x ?? null,
                y: this.yAxisUnitControl.value ?? current.unit?.y ?? null,
            },
            xaxisTitle: {
                show: current.xaxisTitle?.show ?? true,
                text: this.xAxisTitleTextControl.value ?? '',
            },
            yaxisTitle: {
                show: current.yaxisTitle?.show ?? true,
                text: this.yAxisTitleTextControl.value ?? '',
            },
        };
    }

    private async createStatistic(): Promise<void> {
        try {
            await firstValueFrom(
                this.statisticsServiceSdk.statisticControllerPost({
                    createStatisticDto: {
                        aggregationMethod: this.data.payloadStatistic.aggregationMethod ?? undefined,
                        description: this.descriptionControl.value ?? undefined,
                        isShared: this.isSharedControl.value,
                        name: this.nameControl.value,
                        order: Number(this.orderControl.value),
                        render: this.data.payloadStatistic.render!,
                        renderOptions: this.buildUpdatedRenderOptions(),
                        sqlFunction: this.data.payloadStatistic.sqlFunction!,
                        standards: this.data.payloadStatistic.standards ?? [],
                        user: this.data.payloadStatistic.userId!,
                    },
                }),
            );
            this.snackBarService.openSuccessSnackBar('statistic.creationSuccess');
        } catch {
            this.snackBarService.openErrorSnackBar('statistic.creationFailed');
        } finally {
            this.dialogRef.close(this.statisticForm()!);
        }
    }

    private initForm(): void {
        const renderOptions: StatisticRenderOptions = this.data.payloadStatistic.renderOptions ?? {};

        this.addStatisticFormBase(renderOptions);

        if (!this.isAxisConfigurationRequired()) {
            this.removeAxisRowsToForm();
        }
    }

    private removeAxisRowsToForm(): void {
        this.xAxisTitleTextControl.disable({ emitEvent: false });
        this.xAxisUnitControl.disable({ emitEvent: false });
        this.yAxisTitleTextControl.disable({ emitEvent: false });
        this.yAxisUnitControl.disable({ emitEvent: false });

        this.statisticForm()!.patchValue(
            {
                xAxisTitleText: null,
                xAxisUnit: null,
                yAxisTitleText: null,
                yAxisUnit: null,
            },
            { emitEvent: false },
        );

        this.statisticForm()!.updateValueAndValidity({ emitEvent: false });
    }

    private async updateStatistic(): Promise<void> {
        const id = this.data.payloadStatistic.id;

        if (!id) {
            this.snackBarService.openErrorSnackBar('statistic.updateFailed');
            this.dialogRef.close(null);

            return;
        }

        const updateDto: UpdateStatisticDto = {
            description: this.descriptionControl.value ?? null,
            isShared: this.isSharedControl.value,
            name: this.nameControl.value,
            order: Number(this.orderControl.value),
            renderOptions: this.buildUpdatedRenderOptions(),
        };

        try {
            await firstValueFrom(
                this.statisticsServiceSdk.statisticControllerPatch({
                    id,
                    updateStatisticDto: updateDto,
                }),
            );
            this.snackBarService.openSuccessSnackBar('statistic.updateSuccess');
        } catch {
            this.snackBarService.openErrorSnackBar('statistic.updateFailed');
        } finally {
            this.dialogRef.close(this.statisticForm()!);
        }

        return;
    }
}
